import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { IdeaNode, MinimalNode } from "./shared_types";
import { _appendTextToIdeaNode } from "./store_utils";
import { AppendTextToIdeaNodeResponse } from "../pages/SummarySession";

type State = {
  rootNode?: IdeaNode;
  childrenNodes: Record<string, MinimalNode>;
  globalDefaultCollapsed: boolean;
  collapseOverrides: Record<number, boolean>; // per-index collapsed-state overrides; value represents collapsed (true) or expanded (false); if absent then follow global default (globalDefaultCollapsed)
  lastUserAction: 'expand_all' | 'collapse_all' | null;
  initialApplied: boolean;
};

type Actions = {
  setRootNode: (newRootNode: IdeaNode) => void;
  appendTextToIdeaNode: (
    appendTextToIdeaNodeResponse: AppendTextToIdeaNodeResponse
  ) => void;
  appendTextToChildrenNode: (parentPath: string, textToAppend: string) => void;
  toggleChildrenNode: (parentPath: string) => void;
  toggleNodeCollapse: (nodeIndex: string) => void;
  isNodeCollapsed: (nodeIndex: string) => boolean;
  collapseRangeNodes: (startIndex: number, endIndex: number) => void;
  collapseAllNodes: (totalNodes?: number) => void;
  expandAllNodes: () => void;
  expandNode: (index: number) => void;
  collapseNode: (index: number) => void;
  applyInitialCollapseIfNeeded: (params: {
    rehydrated: boolean;
    hasSlug: boolean;
    deepLinked: boolean;
  }) => void;
  cleanState: () => void;
  restoreState: (snapshot: { rootNode: IdeaNode; childrenNodes: Record<string, MinimalNode> }) => void;
};

export const usethreadModeStore = create<State & Actions>()(
  immer((set, get) => ({
    rootNode: undefined,
    childrenNodes: {},
    globalDefaultCollapsed: false,
    collapseOverrides: {},
    lastUserAction: null,
    initialApplied: false,

    setRootNode: (newRootNode: IdeaNode) => {
      set((state) => {
        state.rootNode = newRootNode;
      });
    },
    appendTextToIdeaNode: (
      appendTextToIdeaNodeResponse: AppendTextToIdeaNodeResponse
    ) => {
      set((state) => {
        _appendTextToIdeaNode(state, appendTextToIdeaNodeResponse);
      });
    },
    appendTextToChildrenNode: (parentPath: string, textToAppend: string) => {
      set((state) => {
        if (parentPath in state.childrenNodes) {
          state.childrenNodes[parentPath].text += textToAppend;
        } else {
          const targetNode: MinimalNode = {
            text: textToAppend,
            expanded: true,
          };
          state.childrenNodes[parentPath] = targetNode;
        }
      });
    },
    toggleChildrenNode: (parentPath: string) => {
        set((state) => {
            if (parentPath in state.childrenNodes) {
                state.childrenNodes[parentPath].expanded = !state.childrenNodes[parentPath].expanded
            }
        })
    },
    toggleNodeCollapse: (nodeIndex: string) => {
      set((state) => {
        const idx = parseInt(nodeIndex, 10);
        const current = state.collapseOverrides.hasOwnProperty(idx)
          ? state.collapseOverrides[idx]
          : state.globalDefaultCollapsed;
        const next = !current;
        if (next === state.globalDefaultCollapsed) {
          delete state.collapseOverrides[idx];
        } else {
          state.collapseOverrides[idx] = next;
        }
      });
    },
    isNodeCollapsed: (nodeIndex: string) => {
      const idx = parseInt(nodeIndex, 10);
      const { globalDefaultCollapsed, collapseOverrides } = get();
      const override = collapseOverrides[idx];
      return override !== undefined ? override : globalDefaultCollapsed;
    },
    collapseRangeNodes: (startIndex: number, endIndex: number) => {
      set((state) => {
        if (state.globalDefaultCollapsed) {
          for (let i = startIndex; i < endIndex; i++) {
            if (state.collapseOverrides[i] === false) delete state.collapseOverrides[i];
          }
        } else {
          for (let i = startIndex; i < endIndex; i++) {
            state.collapseOverrides[i] = true;
          }
        }
      });
    },
    collapseAllNodes: (_totalNodes?: number) => {
      set((state) => {
        state.globalDefaultCollapsed = true;
        state.collapseOverrides = {};
        state.lastUserAction = 'collapse_all';
      });
    },
    expandAllNodes: () => {
      set((state) => {
        state.globalDefaultCollapsed = false;
        state.collapseOverrides = {};
        state.lastUserAction = 'expand_all';
      });
    },
    expandNode: (index: number) => {
      set((state) => {
        if (state.globalDefaultCollapsed) {
          state.collapseOverrides[index] = false;
        } else {
          delete state.collapseOverrides[index];
        }
      });
    },
    collapseNode: (index: number) => {
      set((state) => {
        if (state.globalDefaultCollapsed) {
          delete state.collapseOverrides[index];
        } else {
          state.collapseOverrides[index] = true;
        }
      });
    },
    applyInitialCollapseIfNeeded: (params: { rehydrated: boolean; hasSlug: boolean; deepLinked: boolean }) => {
      set((state) => {
        if (state.initialApplied) return;
        if (params.deepLinked) {
          state.globalDefaultCollapsed = true;
          state.collapseOverrides = {};
          state.initialApplied = true;
          return;
        }
        if (params.rehydrated || params.hasSlug) {
          if (state.lastUserAction !== 'expand_all') {
            state.globalDefaultCollapsed = true;
            state.collapseOverrides = {};
          }
          state.initialApplied = true;
        }
      });
    },
    cleanState: () => {
      set((state) => {
        state.rootNode = undefined;
        state.childrenNodes = {};
        state.globalDefaultCollapsed = false;
        state.collapseOverrides = {};
        state.lastUserAction = null;
        state.initialApplied = false;
      })
    },
    restoreState: (snapshot: { rootNode: IdeaNode; childrenNodes: Record<string, MinimalNode> }) => {
      set((state) => {
        state.rootNode = snapshot.rootNode;
        state.childrenNodes = snapshot.childrenNodes;
      })
    },
  }))
);
